import axios from 'axios'
import {deleteLocalStorage, getLocalStorageData,} from '../utils/LocalStorageAccess'
import {AUTH_TOKEN, MAKE_ID} from '../constants/common'
import {deleteSessionStorage} from '../utils/SessionStorage'
import {isAuth} from '../utils/auth'

const formatUrl = (url) => {
  if (url.includes('http')) {
    return url
  }
  return `${process.env.REACT_APP_API_BASE_URL}${url}`
}

// Matches the shape getRequest builds for a paginated response, so list call
// sites can read res.results / res.pagination without guarding every access.
const EMPTY_PAGINATION = {
  currentPage: 1,
  totalCount: 0,
  totalPages: 0,
  prevPageLink: null,
  nextPageLink: null,
}

const emptyListShape = () => ({
  results: [],
  pagination: { ...EMPTY_PAGINATION },
})

// The Authorization header for one request, read from storage at the moment it
// is sent. It used to be written into axios' global defaults by getRequest and
// never removed, so after an in-tab logout the deleted token rode along on every
// later request - including the "no auth" provider redirect, which DRF then
// rejected with 401 "Invalid token." before the view ran. With no token stored,
// nothing is sent; a protected endpoint still answers 401, handled below.
const authHeaders = (isAuthorized = true) =>
  isAuthorized && isAuth() ? {Authorization: `Token ${getLocalStorageData(AUTH_TOKEN)}`} : {}

// Assigning location.href does not stop this script - the navigation is queued
// and runs later. Several requests failing together would each assign it again,
// restarting the pending document load every time. Redirect at most once per
// document, and never to the page we are already on (that is just a reload).
let redirectInFlight = false

const redirectOnce = (path) => {
  if (redirectInFlight || window.location.pathname === path) return
  redirectInFlight = true
  window.location.href = path
}

export const putRequest = async (url, payload) => {
  const data = await withTryCatch(axios.put(formatUrl(url), payload, {headers: authHeaders()}))
  return data
}

export const postRequest = async (
  url,
  payload,
  isDownloadZipFile = false,
  uploadProgressConfig = {}
) => {
  let config = {
    ...uploadProgressConfig,
    headers: {...uploadProgressConfig.headers, ...authHeaders()},
  }

  if (isDownloadZipFile) {
    config['responseType'] = 'blob'
    config['Content-Type'] = 'application/zip'
  }
  const data = await withTryCatch(
    axios.post(formatUrl(url), payload, config),
    isDownloadZipFile
  )
  return data
}

export const deleteRequest = async (url, payload) => {
  // axios.delete takes the config second; keep a caller's own config, add auth
  const data = await withTryCatch(
    axios.delete(formatUrl(url), {...payload, headers: {...payload?.headers, ...authHeaders()}})
  )
  return data
}

export const getRequest = async (
  url,
  isAuthorized = true,
  isDownloadZipFile = false
) => {
  let config = {headers: authHeaders(isAuthorized)}
  if (isDownloadZipFile) {
    config['responseType'] = 'blob'
    config['adapter'] = 'fetch'
    config['Content-Type'] = 'application/zip'
  }
  const data = await withTryCatch(
    axios.get(formatUrl(url), config),
    isDownloadZipFile
  )
  // The 'Invalid token.' check that used to live here is now handled by the
  // 401 branch in withTryCatch, so it applies to every verb rather than GET only.
  if (data?.hasOwnProperty('count')) {
    let currentPage = 1
    let totalPages = 1
    if (data.next) {
      let url = new URL(`${data.next}`)
      currentPage =
        url.searchParams.get('offset') / url.searchParams.get('limit')
      if (url.searchParams.get('limit')) {
        totalPages = data.count / url.searchParams.get('limit')
      } else {
        totalPages = data.count / 10
      }
    } else if (data.previous) {
      let url = new URL(`${data.previous}`)
      currentPage = data.count / url.searchParams.get('limit')
      if (url.searchParams.get('limit')) {
        totalPages = data.count / url.searchParams.get('limit')
      } else {
        totalPages = data.count / 10
      }
    }
    let pagination = {
      currentPage: Number.isInteger(currentPage)
        ? currentPage
        : parseInt(currentPage) + 1,
      totalCount: data.count,
      totalPages: Number.isInteger(totalPages)
        ? totalPages
        : parseInt(totalPages) + 1,
      prevPageLink: data.previous,
      nextPageLink: data.next,
    }
    let results = data.results
    return { results, pagination }
  }
  return data
}

const withTryCatch = async (apiCall, isDownload = false) => {
  try {
    const { data } = await apiCall
    if (isDownload) {
      return data
    } else {
      try {
        if ('status' in data) {
          return { ...data }
        } else {
          return { ...data, status: true }
        }
      } catch (error) {
        return { ...data, status: true }
      }
    }
  } catch (err) {
    // err.response is absent for a network failure, CORS error, timeout or
    // cancelled request. Dereferencing it here used to throw inside the catch,
    // so the caller's await rejected instead of receiving {status: false}.
    const response = err.response
    const httpStatus = response?.status
    const data = response?.data

    if (
      httpStatus === 403 ||
      data?.detail === 'You do not have permission to perform this action.'
    ) {
      redirectOnce('/not_permitted')
      return { ...emptyListShape(), status: false, httpStatus, msg: '' }
    }
    // Only a DRF authentication failure, which always carries `detail`. An
    // app-level 401 (ping answers one with `msg`) is left for the caller to
    // handle - signing out on those would fight with the page that triggered it.
    // Note getRequest interpolates a missing token into the header as the literal
    // `Token null`, which DRF rejects as an invalid token, so the message here is
    // always 'Invalid token.' and never 'Authentication credentials were not provided.'.
    if (httpStatus === 401 && data?.detail) {
      deleteLocalStorage(AUTH_TOKEN)
      deleteLocalStorage(MAKE_ID)
      deleteSessionStorage('vehicle')
      deleteSessionStorage('dtcs_list')
      redirectOnce('/users/sign_in')
      return { ...emptyListShape(), status: false, httpStatus, msg: '' }
    }

    // Read the resolved config, not err.request: the latter is an XHR-only
    // property and downloads use the fetch adapter, where it is always undefined.
    if (err.config?.responseType === 'blob') {
      try {
        const errorText = await data?.text?.()
        const errorJson = JSON.parse(errorText)
        return {
          ...emptyListShape(),
          ...errorJson,
          status: false,
          httpStatus,
          msg: errorJson.msg || '',
        }
      } catch {
        return {
          ...emptyListShape(),
          status: false,
          httpStatus,
          msg: 'Data not available or Something went wrong, please try after some time.',
        }
      }
    }

    let msg = ''
    // `typeof null === 'object'`, so null has to be excluded explicitly.
    if (data && typeof data === 'object') {
      if (data.msg) {
        msg = data.msg
      } else {
        msg = Object.keys(data)
          .map((key) => `${key} ${data[key]}`)
          .join(', ')
      }
    }

    // Defaults first so a real server body always wins over them.
    return { ...emptyListShape(), ...(data || {}), status: false, httpStatus, msg }
  }
}
